import { useState, useCallback, useRef, useEffect } from 'react';

interface HtmlEditorModalProps {
  isOpen: boolean;
  onInsert: (html: string) => void;
  onCancel: () => void;
}

export function HtmlEditorModal({ isOpen, onInsert, onCancel }: HtmlEditorModalProps) {
  const [html, setHtml] = useState('');
  const [preview, setPreview] = useState('');
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const schedulePreviewUpdate = useCallback((value: string) => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => setPreview(value), 250);
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setHtml(value);
      schedulePreviewUpdate(value);
    },
    [schedulePreviewUpdate],
  );

  const handleInsert = useCallback(() => {
    const trimmed = html.trim();
    if (!trimmed) return;
    onInsert(trimmed);
  }, [html, onInsert]);

  // Reset on close
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen) {
      setHtml('');
      setPreview('');
    }
  }

  // ESC to cancel
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="html-editor-modal-title"
    >
      <div
        className="flex w-full max-w-4xl flex-col rounded-lg bg-card shadow-xl"
        style={{ height: '85vh' }}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-muted-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
              />
            </svg>
            <h2
              id="html-editor-modal-title"
              className="text-lg font-semibold text-foreground"
            >
              HTML Block
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="Cancel"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Split body */}
        <div className="flex min-h-0 flex-1 flex-col">
          {/* Preview pane — top 40% */}
          <div className="flex flex-col border-b border-border" style={{ flex: '0 0 40%' }}>
            <div className="flex shrink-0 items-center gap-1.5 border-b border-border/50 bg-muted/40 px-4 py-2">
              <svg
                className="h-3.5 w-3.5 text-muted-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Preview
              </span>
            </div>
            <div className="flex-1 overflow-auto bg-white">
              {preview ? (
                <iframe
                  srcDoc={preview}
                  className="h-full w-full border-0"
                  title="HTML preview"
                  sandbox="allow-same-origin"
                />
              ) : (
                <div className="flex h-full items-center justify-center">
                  <p className="text-sm text-muted-foreground">
                    Preview appears here as you type…
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Editor pane — remaining space */}
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex shrink-0 items-center gap-1.5 border-b border-border/50 bg-muted/40 px-4 py-2">
              <svg
                className="h-3.5 w-3.5 text-muted-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                />
              </svg>
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                HTML
              </span>
            </div>
            <textarea
              value={html}
              onChange={handleChange}
              placeholder="<p>Write your custom HTML here…</p>"
              spellCheck={false}
              autoFocus
              className="flex-1 resize-none bg-background px-4 py-3 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border px-6 py-4">
          <button
            onClick={onCancel}
            className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleInsert}
            disabled={!html.trim()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          >
            Insert Block
          </button>
        </div>
      </div>
    </div>
  );
}
